import { useState, useEffect, useMemo } from "react";
import { Trash2, Plus, Info } from "lucide-react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import ToggleSwitch from "../../BaseComponents/Toggle/ToggleSwitch";
import SearchableSelect from "../../BaseComponents/SearchableSelect/SearchableSelect";
import { useToast } from "../../../context/ToastContext";
import { useGetCities } from "../../../api/geo/useCities";
import { getZones } from "../../../api/geo/useZones";
import styles from "./AddEditAirportModal.module.css";

const emptyForm = {
  name: "",
  code: "",
  countryId: "",
  latitude: "",
  longitude: "",
  active: true,
  flightConnectionPolicy: "DIRECT_ONLY",
  // Master §12B.6: permitted origin, and "popular" (UI order only — never prices or feasibility).
  isDeparture: false,
  isPopularDeparture: false,
};

// Which flights a package to this airport may use (stored per airport; used by packages later).
const CONNECTION_POLICIES = [
  {
    value: "DIRECT_ONLY",
    label: "Direct flights only",
    hint: "Only show direct (nonstop) flights for this airport.",
  },
  {
    value: "DIRECT_OR_MAX_1_STOP",
    label: "Direct flights + maximum 1 stop",
    hint: "Allow both direct flights and flights with up to 1 stop.",
  },
];

// "Istanbul (IST)": every linked city and zone is shown with its code behind the name.
const withCode = (name, code) => (code ? `${name} (${code})` : name);

// Parse the persisted linkedCities value into [{ id, name, code, zones?: [{id,name,code}] }].
// `code === undefined` (data saved before codes were stored) → filled in on load.
// Backward-compatible: JSON array of objects (new — may carry zones), legacy
// comma-separated names, or a plain array. `zones === undefined` means "not yet
// known" → we lazily derive them from the city on load.
const normalizeZones = (zones) => {
  if (!Array.isArray(zones)) return undefined;
  return zones
    .map((z) =>
      typeof z === "object" && z !== null
        ? { id: z.id ?? null, name: z.name ?? String(z.id ?? ""), code: "code" in z ? z.code : undefined }
        : { id: null, name: String(z), code: undefined }
    )
    .filter((z) => z.name);
};

// A zone from the zones API, as it is kept on a linked city.
const toZone = (z) => ({ id: z.id, name: z.name, code: z.zoneCode ?? null });

// A linked city whose zones, or the zones' codes, still have to be fetched.
const needsZoneLookup = (c) =>
  c.id != null && (c.zones === undefined || c.zones.some((z) => z.code === undefined));

const parseLinkedCities = (value) => {
  if (value == null || value === "") return [];

  const fromNames = (str) =>
    str
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((name) => ({ id: null, name, zones: undefined }));

  let arr = value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("[")) {
      try {
        arr = JSON.parse(trimmed);
      } catch {
        return fromNames(trimmed);
      }
    } else {
      return fromNames(trimmed);
    }
  }

  if (!Array.isArray(arr)) return [];
  return arr
    .map((v) =>
      typeof v === "object" && v !== null
        ? {
            id: v.id ?? null,
            name: v.name ?? String(v.id ?? ""),
            code: v.code ?? undefined,
            zones: normalizeZones(v.zones),
          }
        : { id: null, name: String(v), zones: undefined }
    )
    .filter((c) => c.name);
};

const AddEditAirportModal = ({
  open,
  onClose,
  mode = "add",
  initialData,
  countries = [],
  onSave,
  saveLoading = false,
}) => {
  const { showToast } = useToast();
  const [form, setForm] = useState(emptyForm);
  // [{ id, name, code, zones: [{ id, name, code }] }]
  const [linkedCities, setLinkedCities] = useState([]);
  const [cityToAdd, setCityToAdd] = useState("");
  const [addingCity, setAddingCity] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Cities for the dropdown — filtered by the selected country when one is set.
  const { data: citiesData = [] } = useGetCities({
    page: 1,
    limit: 500,
    countryId: form.countryId || undefined,
    sortBy: "name",
    sortOrder: "ASC",
  });
  const cityOptions = useMemo(() => (Array.isArray(citiesData) ? citiesData : []), [citiesData]);

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    setCityToAdd("");

    if (mode === "edit" && initialData) {
      setForm({
        name: initialData.name || "",
        code: initialData.code || "",
        countryId: initialData.countryId ?? "",
        latitude: initialData.latitude ?? initialData.lat ?? "",
        longitude: initialData.longitude ?? initialData.lng ?? "",
        active: initialData.active ?? true,
        // MAX_ONE_STOP is the first version's name for DIRECT_OR_MAX_1_STOP.
        flightConnectionPolicy: initialData.flightConnectionPolicy === "MAX_ONE_STOP"
          ? "DIRECT_OR_MAX_1_STOP"
          : initialData.flightConnectionPolicy || "DIRECT_ONLY",
        isDeparture: !!Number(initialData.isDeparture ?? 0),
        isPopularDeparture: !!Number(initialData.isPopularDeparture ?? 0),
      });
      setLinkedCities(parseLinkedCities(initialData.linkedCities));
    } else {
      setForm(emptyForm);
      setLinkedCities([]);
    }
  }, [open, mode, initialData]);

  // Backfill zones for linked cities that don't carry them yet (legacy data
  // saved before zones were persisted), and zone codes for zones saved before codes
  // were. Cities with no id can't be derived.
  useEffect(() => {
    if (!open) return;
    const missing = linkedCities.filter(needsZoneLookup);
    if (missing.length === 0) return;

    let cancelled = false;
    Promise.all(
      missing.map((c) =>
        getZones({ destinationId: c.id, page: 1, limit: 500 })
          .then((res) => ({
            cityId: c.id,
            zones: res?.success && Array.isArray(res.data) ? res.data.map(toZone) : [],
          }))
          .catch(() => ({ cityId: c.id, zones: [] }))
      )
    ).then((results) => {
      if (cancelled) return;
      setLinkedCities((prev) =>
        prev.map((c) => {
          if (!needsZoneLookup(c)) return c;
          const found = results.find((r) => String(r.cityId) === String(c.id));
          if (!found) return c;
          if (c.zones === undefined) return { ...c, zones: found.zones };
          // Keep exactly the zones that were saved (some may have been removed on purpose);
          // only add their codes. null = looked up, the zone has no code.
          return {
            ...c,
            zones: c.zones.map((z) => ({
              ...z,
              code: z.code !== undefined ? z.code : found.zones.find((f) => String(f.id) === String(z.id))?.code ?? null,
            })),
          };
        })
      );
    });

    return () => {
      cancelled = true;
    };
  }, [open, linkedCities]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCountryChange = (value) => {
    setForm((prev) => ({ ...prev, countryId: value }));
    setCityToAdd("");
  };

  const availableCityOptions = useMemo(() => {
    const linkedIds = new Set(linkedCities.map((c) => c.id).filter((id) => id != null));
    return cityOptions
      .filter((c) => !linkedIds.has(c.id))
      .map((c) => ({ label: withCode(c.name, c.code), value: String(c.id) }));
  }, [cityOptions, linkedCities]);

  // A city saved before codes were stored takes its code from the city list.
  const cityCode = (c) =>
    c.code ?? cityOptions.find((o) => String(o.id) === String(c.id))?.code ?? null;

  // Flatten the linked cities' zones (deduped) for the Linked Zones list.
  const linkedZones = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of linkedCities) {
      for (const z of c.zones || []) {
        if (z && z.id != null && !seen.has(z.id)) {
          seen.add(z.id);
          // Each zone also names its city's code (Levent): "Algarrobo Costa (4) · MLG".
          const code = c.code ?? cityOptions.find((o) => String(o.id) === String(c.id))?.code ?? null;
          out.push({ ...z, cityId: c.id, cityName: c.name, cityCode: code });
        }
      }
    }
    // Grouped by city code, then by zone code (numerically: 2 before 10), so a city's zones sit
    // together in code order (Levent). Name breaks ties and orders zones without a code.
    const zoneNum = (z) => (z.code == null || z.code === "" ? Infinity : Number(z.code));
    out.sort((a, b) =>
      String(a.cityCode ?? "").localeCompare(String(b.cityCode ?? ""))
      || (Number.isNaN(zoneNum(a)) || Number.isNaN(zoneNum(b))
        ? String(a.code ?? "").localeCompare(String(b.code ?? ""))
        : zoneNum(a) - zoneNum(b))
      || (a.name || "").localeCompare(b.name || ""));
    return out;
  }, [linkedCities, cityOptions]);

  const handleAddCity = async () => {
    if (!cityToAdd) {
      showToast("Select a city to add", "error");
      return;
    }
    const city = cityOptions.find((c) => String(c.id) === String(cityToAdd));
    if (!city) {
      showToast("Selected city is not available", "error");
      return;
    }
    if (linkedCities.some((c) => String(c.id) === String(city.id))) {
      showToast(`${city.name} is already linked`, "error");
      return;
    }

    // Auto-link all the city's zones (the user can remove individual ones after).
    setAddingCity(true);
    try {
      const res = await getZones({ destinationId: city.id, page: 1, limit: 500 }).catch(() => null);
      const zones = res?.success && Array.isArray(res.data) ? res.data.map(toZone) : [];
      setLinkedCities((prev) => [...prev, { id: city.id, name: city.name, code: city.code ?? null, zones }]);
      setCityToAdd("");
      showToast(`${city.name} added`, "success");
    } finally {
      setAddingCity(false);
    }
  };

  const handleRemoveCity = (city) => {
    setLinkedCities((prev) =>
      prev.filter((c) =>
        c.id != null ? String(c.id) !== String(city.id) : c.name !== city.name
      )
    );
    showToast(`${city.name} removed`, "success");
  };

  const handleRemoveZone = (zone) => {
    setLinkedCities((prev) =>
      prev.map((c) => ({
        ...c,
        zones: (c.zones || []).filter((z) => String(z.id) !== String(zone.id)),
      }))
    );
    showToast(`${zone.name} removed`, "success");
  };

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      showToast("Airport name is required", "error");
      return;
    }
    if (!form.code?.trim() || form.code.length !== 3) {
      showToast("IATA code must be 3 characters", "error");
      return;
    }
    setSubmitError(null);

    // Persist cities WITH their (possibly trimmed) zones as a JSON array.
    const citiesToPersist = linkedCities.map((c) => ({
      id: c.id,
      name: c.name,
      code: cityCode(c),
      zones: (c.zones || []).map((z) => ({ id: z.id, name: z.name, code: z.code ?? null })),
    }));
    const linkedCitiesValue =
      citiesToPersist.length > 0 ? JSON.stringify(citiesToPersist) : null;

    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      linkedCities: linkedCitiesValue,
      latitude:
        typeof form.latitude === "string"
          ? form.latitude.trim() || null
          : form.latitude ?? null,
      longitude:
        typeof form.longitude === "string"
          ? form.longitude.trim() || null
          : form.longitude ?? null,
      countryId: form.countryId ? Number(form.countryId) : null,
      active: form.active,
      flightConnectionPolicy: form.flightConnectionPolicy,
      isDeparture: form.isDeparture,
      // Popular only on an active departure airport (the server refuses anything else).
      isPopularDeparture: form.isDeparture && form.active && form.isPopularDeparture,
    };
    try {
      await onSave(payload);
      onClose();
    } catch (err) {
      setSubmitError(err.message || "Failed to save airport");
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={mode === "add" ? "Add Airport" : "Edit Airport"}
      width="960px"
      footer={
        <>
          {submitError && (
            <span className={styles.submitError}>{submitError}</span>
          )}
          <button
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={saveLoading}
          >
            Cancel
          </button>
          <button
            className={`${styles.primaryBtn} ${saveLoading ? styles.primaryBtnLoading : ""}`}
            onClick={handleSubmit}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <span className={styles.spinner} aria-hidden />
                Saving…
              </>
            ) : (
              "Save Airport"
            )}
          </button>
        </>
      }
    >
      <div className={styles.formGrid}>
        {/* ── Left column — airport identity ──────────────────────────── */}
        <div className={styles.leftCol}>
          <div className={styles.field}>
            <label>Airport Name</label>
            <input
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="e.g. Brussels, Brussels National Airport"
            />
          </div>

          <div className={styles.field}>
            <label>IATA</label>
            <input
              maxLength={3}
              value={form.code}
              onChange={(e) =>
                handleChange("code", e.target.value.toUpperCase())
              }
              placeholder="e.g. BRU"
            />
          </div>

          <div className={styles.field}>
            <label>Country</label>
            <SearchableSelect
              placeholder="Select country"
              searchPlaceholder="Search country..."
              width="100%"
              value={String(form.countryId)}
              options={[
                { label: "No country", value: "" },
                ...countries.map((c) => ({
                  label: c.name,
                  value: String(c.id),
                })),
              ]}
              onChange={(val) => handleCountryChange(val)}
            />
          </div>

          <div className={styles.latLngRow}>
            <div className={styles.field}>
              <label>Latitude</label>
              <input
                value={form.latitude}
                onChange={(e) => handleChange("latitude", e.target.value)}
                placeholder="e.g. 50.9014"
              />
            </div>
            <div className={styles.field}>
              <label>Longitude</label>
              <input
                value={form.longitude}
                onChange={(e) => handleChange("longitude", e.target.value)}
                placeholder="e.g. 4.4844"
              />
            </div>
          </div>

          <div className={styles.statusRow}>
            <label>Active</label>
            <ToggleSwitch
              checked={form.active}
              onChange={() => handleChange("active", !form.active)}
            />
          </div>

          <div className={styles.statusRow}>
            <label>SUNSKY Departure Airport</label>
            <ToggleSwitch
              checked={form.isDeparture}
              onChange={() => handleChange("isDeparture", !form.isDeparture)}
            />
          </div>
          <div className={styles.statusRow}>
            <label title="Shown first in the departure airport lists. Never changes prices or which airports a search compares.">
              Popular Departure Airport
            </label>
            <ToggleSwitch
              checked={form.isDeparture && form.active && form.isPopularDeparture}
              disabled={!form.isDeparture || !form.active}
              onChange={() => handleChange("isPopularDeparture", !form.isPopularDeparture)}
            />
          </div>

          <fieldset className={styles.policyBox} disabled={form.isDeparture} aria-disabled={form.isDeparture}>
            <legend className={styles.policyTitle}>
              Flight connection Policy for Package Holidays
              <span
                className={styles.policyInfo}
                title="Which flights package holidays to this airport may use. Applies to package holidays only, not to flight-only bookings."
                aria-label="Which flights package holidays to this airport may use"
              >
                <Info size={15} />
              </span>
            </legend>
            {form.isDeparture && (
              <p className={styles.policyHint} role="note">
                Not used for a departure airport. Package routing follows the destination&apos;s
                Flight Connection Policy (GeoData → Destination airports). The stored value is kept.
              </p>
            )}
            {CONNECTION_POLICIES.map((p) => (
              <label key={p.value} className={styles.policyOption}>
                <input
                  type="radio"
                  name="flightConnectionPolicy"
                  value={p.value}
                  checked={form.flightConnectionPolicy === p.value}
                  onChange={() => handleChange("flightConnectionPolicy", p.value)}
                />
                <span>
                  <span className={styles.policyLabel}>{p.label}</span>
                  <span className={styles.policyHint}>{p.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
        </div>

        {/* ── Right column — linked cities & zones ────────────────────── */}
        <div className={styles.rightCol}>
          {/* Linked Cities */}
          <div className={styles.sectionLabel}>Linked Cities</div>

          <div className={styles.citiesCard}>
            <div className={styles.addRow}>
              <SearchableSelect
                placeholder="Select city"
                searchPlaceholder="Search city..."
                width="100%"
                value={cityToAdd}
                options={availableCityOptions}
                onChange={(val) => setCityToAdd(val)}
              />
              <button
                type="button"
                className={styles.addCityBtn}
                onClick={handleAddCity}
                disabled={addingCity}
              >
                <Plus size={16} /> {addingCity ? "Adding…" : "Add City"}
              </button>
            </div>

            <div className={styles.tableHead}>City</div>

            {linkedCities.length === 0 ? (
              <div className={styles.emptyHint}>No cities linked yet.</div>
            ) : (
              <ul className={styles.cityRows}>
                {linkedCities.map((c) => (
                  <li
                    key={c.id != null ? `id-${c.id}` : `name-${c.name}`}
                    className={styles.cityRow}
                  >
                    <span className={styles.cityName}>{withCode(c.name, cityCode(c))}</span>
                    <button
                      type="button"
                      className={styles.rowRemove}
                      onClick={() => handleRemoveCity(c)}
                      aria-label={`Remove ${c.name}`}
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Info note */}
          <div className={styles.infoNote}>
            <Info size={16} className={styles.infoIcon} />
            <span>
              All zones under a linked city are added automatically. Remove any
              you don't need.
            </span>
          </div>

          {/* Linked Zones */}
          <div className={styles.sectionLabel}>Linked Zones</div>

          <div className={styles.zonesCard}>
            <div className={styles.tableHead}>Zone</div>

            {addingCity ? (
              <div className={styles.emptyHint}>Loading zones…</div>
            ) : linkedZones.length === 0 ? (
              <div className={styles.emptyHint}>
                {linkedCities.length === 0
                  ? "Link cities to see their zones here."
                  : "No zones found for the linked cities."}
              </div>
            ) : (
              <ul className={styles.zoneRows}>
                {linkedZones.map((z) => (
                  <li key={z.id} className={styles.zoneRow}>
                    <span className={styles.zoneName}>{withCode(z.name, z.code)}{z.cityCode ? ` · ${z.cityCode}` : ""}</span>
                    <button
                      type="button"
                      className={styles.rowRemove}
                      onClick={() => handleRemoveZone(z)}
                      aria-label={`Remove ${z.name}`}
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddEditAirportModal;
